import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  HeadingText,
  Icon,
  Image,
  StackLayout,
  useMediaQuery,
  queryHelpers,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useDocumentTitle } from '@leagueplatform/web-common';
import permissionIconSvg from 'assets/permission-icon.svg';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { ConnectionErrorPage } from 'pages/errors/connection-error/connection-error.page';
import { useSetCaregiverOnboardingComplete } from 'hooks/use-set-caregiver-onboarding-complete.hook';
import { OnboardingPageWrapper } from 'components/onboarding-page-wrapper/onboarding-page-wrapper.component';

const CAREGIVER_PERMISSIONS = [
  {
    id: 'permission-1',
    labelId: 'STR_CAREGIVER_PERMISSION_COMPLETE_CHECK_INS',
  },
  { id: 'permission-2', labelId: 'STR_CAREGIVER_PERMISSION_REVIEW_NEXT_STEPS' },
  { id: 'permission-3', labelId: 'STR_CAREGIVER_PERMISSION_GET_AI_ANSWERS' },
] as const;

export const CaregiverPermissionsPage = () => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);

  const { mutateAsync: setCaregiverOnboardingComplete, isPending } =
    useSetCaregiverOnboardingComplete();

  useDocumentTitle(formatMessage({ id: 'STR_HOW_YOU_CAN_HELP' }));

  const onContinue = async () => {
    try {
      setShowError(false);
      await setCaregiverOnboardingComplete();
      navigate(APP_MANAGED_ROUTES.HOME);
    } catch {
      setShowError(true);
    }
  };

  if (showError) {
    return <ConnectionErrorPage retry={onContinue} />;
  }

  return (
    <OnboardingPageWrapper
      buttonTitle={formatMessage({ id: 'STR_LETS_GET_STARTED' })}
      onButtonClick={onContinue}
      isButtonDisabled={isPending}
    >
      <StackLayout
        horizontalAlignment="center"
        css={{
          padding: isMobile
            ? '$five $oneAndHalf $five'
            : '$twoAndHalf $twoAndHalf $oneAndHalf',
          gap: '$threeQuarters',
        }}
      >
        <Image src={permissionIconSvg} alt="" width="176" height="176" />

        <HeadingText as="h1" level="1" size="xl" css={{ textAlign: 'center' }}>
          {formatMessage({ id: 'STR_HOW_YOU_CAN_HELP' })}
        </HeadingText>

        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '$oneAndHalf',
            alignItems: isMobile ? 'flex-start' : 'center',
            paddingBlockEnd: '$oneAndHalf',
          }}
        >
          <UtilityText
            css={{
              textAlign: 'center',
              color: '$onSurfaceTextSubdued',
              paddingBlockEnd: '$half',
            }}
          >
            {formatMessage({ id: 'STR_CAREGIVER_PERMISSIONS_SUBTITLE' })}
          </UtilityText>

          {CAREGIVER_PERMISSIONS.map(({ id, labelId }) => (
            <Box
              key={id}
              css={{
                display: 'flex',
                width: '332px',
                gap: '$one',
              }}
            >
              <Icon
                icon="tinyValidationCheck"
                css={{ flexShrink: 0, padding: '$eighth' }}
              />
              <UtilityText>{formatMessage({ id: labelId })}</UtilityText>
            </Box>
          ))}
        </Box>
      </StackLayout>
    </OnboardingPageWrapper>
  );
};
